import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ChatCircleText, MagnifyingGlass } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { TemplatePicker } from "../TemplatePicker";
import { substituteTokens } from "../../lib/templateTokens";
import { LiveSuggestion, TranslateDraftButton } from "./ChatAssist";

const inputCls = "w-full h-10 px-3 border border-[#E5E2D9] rounded-md text-sm bg-white focus:outline-none focus:border-[#DC2626]";

export const StartChatButton = ({ onStarted, initialCustomerId, onClearInitial }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [picked, setPicked] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [ctx, setCtx] = useState({});

  useEffect(() => {
    if (!initialCustomerId) return;
    api.get(`/customers/${initialCustomerId}`)
      .then(r => { setPicked(r.data); setOpen(true); })
      .catch(e => toast.error(formatApiError(e)))
      .finally(() => onClearInitial?.());
  }, [initialCustomerId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!picked) { setCtx({}); return; }
    api.get(`/chat/assist/customer-context/${picked.id}`).then(r => setCtx(r.data)).catch(() => setCtx({}));
  }, [picked]);

  useEffect(() => {
    const s = q.trim();
    if (!s || picked) { setResults([]); return; }
    setLoading(true);
    let live = true;
    const t = setTimeout(() => {
      api.get("/customers", { params: { q: s, limit: 20 } })
        .then(r => live && setResults(r.data))
        .catch(e => live && toast.error(formatApiError(e)))
        .finally(() => live && setLoading(false));
    }, 300);
    return () => { live = false; clearTimeout(t); };
  }, [q, picked]);

  const reset = () => { setQ(""); setPicked(null); setText(""); setResults([]); };
  const start = async () => {
    setBusy(true);
    try {
      const r = await api.post("/chat/conversations/start", { customer_id: picked.id, text: text.trim() });
      toast.success(`Chat started with ${picked.name}`);
      setOpen(false); reset();
      onStarted(r.data.conversation.id);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="chat-start-btn"
              className="h-9 px-3 rounded-md bg-[#DC2626] hover:bg-[#B91C1C] text-white text-xs font-bold inline-flex items-center gap-1.5">
        <ChatCircleText size={14} weight="fill" /> New chat
      </button>
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
        <DialogContent data-testid="chat-start-dialog">
          <DialogHeader><DialogTitle>Start a chat with a customer</DialogTitle></DialogHeader>
          {picked ? (
            <div className="flex items-center gap-2 p-2.5 border border-[#DC2626] bg-[#FEF2F2] rounded-md text-sm" data-testid="chat-start-picked">
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">{picked.name}</div>
                <div className="text-[11px] text-[#5C5A55]">{picked.customer_code} · {picked.phone}</div>
              </div>
              <button onClick={() => setPicked(null)} className="text-xs underline" data-testid="chat-start-change">Change</button>
            </div>
          ) : (
            <div>
              <div className="relative">
                <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8780]" />
                <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, phone or customer code"
                       className={`${inputCls} pl-9`} data-testid="chat-start-search" />
              </div>
              <div className="mt-2 max-h-64 overflow-y-auto border border-[#F0EEE7] rounded-md divide-y divide-[#F0EEE7]" data-testid="chat-start-results">
                {loading && <div className="p-3 text-xs text-[#8A8780]">Searching…</div>}
                {!loading && q.trim() && results.length === 0 && <div className="p-3 text-xs text-[#8A8780]">No customers match.</div>}
                {!q.trim() && <div className="p-3 text-xs text-[#8A8780]">Type to search customers.</div>}
                {results.map(c => (
                  <button key={c.id} onClick={() => setPicked(c)} data-testid={`chat-start-opt-${c.id}`}
                          className="w-full text-left p-2.5 hover:bg-[#F7F6F2] text-sm">
                    <div className="font-medium">{c.name}</div>
                    <div className="text-[11px] text-[#5C5A55]">{c.customer_code} · {c.phone}</div>
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-[0.18em] text-[#5C5A55]">First message</span>
            <TemplatePicker onPick={(t) => {
              setText(substituteTokens(t.body, ctx));
              api.post(`/reply-templates/${t.id}/use`).catch(() => {});
            }} />
          </div>
          <LiveSuggestion draft={text} onUse={setText} />
          <div className="flex gap-2 items-start">
            <textarea rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder="First message"
                      className={`${inputCls} h-auto py-2 flex-1`} data-testid="chat-start-text" />
            <TranslateDraftButton draft={text} setDraft={setText} />
          </div>
          <button onClick={start} disabled={!picked || !text.trim() || busy} data-testid="chat-start-send"
                  className="h-10 rounded-md bg-[#DC2626] text-white text-sm font-bold disabled:opacity-50">
            {busy ? "Starting…" : "Start chat"}
          </button>
        </DialogContent>
      </Dialog>
    </>
  );
};
